import React, { useState, useMemo } from "react";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import ProgramForm from "@pages/Program/components/ProgramForm";
import { useForm, FormProvider } from "react-hook-form";
import { useUnsavedChangesWarning } from "@shared/components/UnsavedChangesPopup/useUnsavedChangesWarning";
import UnsavedChangesModal from "@shared/components/UnsavedChangesPopup/UnsavedChangesModal";
import { ProgramPayload } from "@shared/interfaces/ProgramFiles";
import { toastService } from "@services/ToastService";
import useHandleSaveProgram from "@pages/Program/utils/useHandleSaveProgram";
import { useUser } from "@services/UserContext";
import NewItemButton from "@shared/components/NewItemButton";

const CreateProgramPage: React.FC = () => {
  const { user } = useUser();
  const [submitting, setSubmitting] = useState(false);

  const defaultValues = useMemo(() => {
    return {
      name: "",
      icon: undefined,
      description: "",
      donors: [],
      budget: "",
      start_date: "",
      end_date: "",
      members: user
        ? [
            {
              user_id: user.id,
              leader: true,
            },
          ]
        : [],
      currency_id: undefined as unknown as number,
    };
  }, [user]);

  const methods = useForm<any>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues,
  });

  const {
    handleSubmit,
    getValues,
    formState: { isValid },
  } = methods;

  methods.watch();

  const handleSaveProgram = useHandleSaveProgram({}, methods);

  const onSave = async (values: ProgramPayload) => {
    if (!isValid) return;
    setSubmitting(true);

    try {
      const data: ProgramPayload = {
        ...values,
        currency_id: values.currency_id,
        reporting_frequency: null,
      };
      await handleSaveProgram(data);
    } catch (error) {
      console.error("Error saving program:", error);
      toastService.error("Failed to create program");
    } finally {
      setSubmitting(false);
    }
  };

  const { blocker, handleProceed, handleCancel, isBlocked } =
    useUnsavedChangesWarning(getValues(), methods.control._defaultValues);

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal
        onProceed={handleProceed}
        onCancel={handleCancel}
        isOpen={isBlocked}
      />

      <form
        onSubmit={handleSubmit(onSave)}
        className="w-full h-[calc(100vh-72px)] flex flex-col relative"
      >
        <div className="flex flex-col h-[calc(100vh-72px)] relative">
          {/* Main Content */}
          <div className="flex flex-1 px-8">
            <div className="flex-1 flex flex-col">
              <SubTitleHeader
                title="Create Program"
                spacer={true}
                children={
                  <NewItemButton
                    showIcon={false}
                    type="submit"
                    title="Create"
                    disabled={!isValid || submitting}
                    isLoading={submitting}
                  />
                }
              />

              <div className="flex flex-1 pt-5">
                <div className="flex-1">
                  <ProgramForm isEditing={true} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateProgramPage;
